import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/utils/api";
import SEOHead, {
  generateBreadcrumbSchema,
  generateItemListSchema,
  generateWebPageSchema,
} from "@/components/SEOHead";
import HelmetSEO from "@/components/HelmetSEO";
import { ChevronRight, ChevronLeft, Star } from "lucide-react";

const ADS_PER_PAGE = 25;

const SeoCategoryPage = () => {
  const params = useParams();
  const city = params.city || "india";
  const category = params.category;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityName, setCityName] = useState(city);
  const [categoryName, setCategoryName] = useState(category);
  const [totalAds, setTotalAds] = useState(null);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  const canonicalUrl = useMemo(() => {
    return `https://uplino.in/${city}/${category}`;
  }, [city, category]);

  const title = useMemo(() => {
    const cityLabel = city === "india" ? "India" : cityName;
    return `${categoryName} in ${cityLabel} | uplino.in`;
  }, [categoryName, city, cityName]);

  const description = useMemo(() => {
    const cityLabel = city === "india" ? "India" : cityName;
    return `Browse ${categoryName} listings in ${cityLabel}. View latest profiles with photos and contact options on uplino.in.`;
  }, [categoryName, city, cityName]);

  const schema = useMemo(() => {
    const cityLabel = city === "india" ? "India" : cityName;
    return {
      "@context": "https://schema.org",
      "@graph": [
        generateWebPageSchema(title, description, canonicalUrl),
        generateBreadcrumbSchema([
          { name: "Home", url: "https://uplino.in" },
          { name: cityLabel, url: `https://uplino.in/${city}` },
          { name: categoryName, url: canonicalUrl },
        ]),
        ...(ads.length > 0 ? [generateItemListSchema(ads, cityLabel, categoryName)] : []),
      ],
    };
  }, [ads, canonicalUrl, categoryName, city, cityName, description, title]);

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);

        // Load city/category names for nicer meta (best-effort)
        const [citiesRes, categoriesRes] = await Promise.all([
          api.get("/cities"),
          api.get("/categories"),
        ]);

        if (city !== "india") {
          const c = citiesRes.data.find((x) => x.slug === city);
          if (c?.name) setCityName(c.name);
        } else {
          setCityName("India");
        }

        const cat = categoriesRes.data.find((x) => x.slug === category);
        if (cat?.name) setCategoryName(cat.name);

        const skip = (currentPage - 1) * ADS_PER_PAGE;
        const adsRes = await api.get(
          `/seo/${encodeURIComponent(city)}/${encodeURIComponent(category)}?skip=${skip}&limit=${ADS_PER_PAGE}`
        );
        setAds(adsRes.data || []);

        // Optional total count (can be added later). For now, derive if last page.
        setTotalAds(null);
      } catch (e) {
        setAds([]);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [city, category, currentPage]);

  const globalPinnedAds = ads.filter((a) => a.is_global_pinned);
  const regularAds = ads.filter((a) => !a.is_global_pinned);

  const handlePrev = () => {
    if (currentPage > 1) setSearchParams({ page: String(currentPage - 1) });
  };

  const handleNext = () => {
    // Without a count endpoint, we can only page forward if we received a full page.
    if (ads.length === ADS_PER_PAGE) setSearchParams({ page: String(currentPage + 1) });
  };

  const cityLabel = city === "india" ? "India" : cityName;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <HelmetSEO title={title} description={description} canonical={canonicalUrl} />
      <SEOHead title={title} description={description} canonical={canonicalUrl} schema={schema} />
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <nav aria-label="Breadcrumb" className="mb-4 sm:mb-6">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-400 hover:text-white">
                Home
              </Link>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <li>
              <Link to={`/${city}`} className="text-gray-400 hover:text-white">
                {cityLabel}
              </Link>
            </li>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <li>
              <span className="text-white font-medium">{categoryName}</span>
            </li>
          </ol>
        </nav>

        <header className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
            {categoryName} in {cityLabel}
          </h1>
          <p className="text-base sm:text-lg text-gray-400">
            {totalAds != null ? `${totalAds} listings` : `Page ${currentPage}`}
          </p>
        </header>

        <section>
          {loading ? (
            <div className="text-center py-20">
              <div className="text-white text-xl">Loading listings...</div>
            </div>
          ) : ads.length === 0 ? (
            <Card className="p-8 sm:p-12 bg-white/5 backdrop-blur-sm border-white/10 text-center">
              <p className="text-gray-400 text-base sm:text-lg mb-2">No listings found</p>
              <p className="text-gray-500 text-sm">Try another city or category.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {globalPinnedAds.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <h2 className="text-lg font-semibold text-white">Featured Ads</h2>
                  </div>
                  <div className="space-y-3">
                    {globalPinnedAds.map((ad) => (
                      <Card
                        key={ad.id}
                        className="p-4 bg-white shadow cursor-pointer"
                        onClick={() => navigate(`/${city}/${category}/${ad.slug}`)}
                      >
                        <div className="font-semibold text-pink-600">{ad.title}</div>
                        <div className="text-sm text-gray-600 line-clamp-2">{ad.description}</div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {regularAds.length > 0 && (
                <div className="space-y-3">
                  {regularAds.map((ad) => (
                    <Card
                      key={ad.id}
                      className="p-4 bg-white shadow cursor-pointer"
                      onClick={() => navigate(`/${city}/${category}/${ad.slug}`)}
                    >
                      <div className="font-semibold text-pink-600">{ad.title}</div>
                      <div className="text-sm text-gray-600 line-clamp-2">{ad.description}</div>
                    </Card>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-center gap-2 mt-8 pt-6 border-t border-white/10">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                </Button>
                <div className="text-gray-300 text-sm px-3">Page {currentPage}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNext}
                  disabled={ads.length < ADS_PER_PAGE}
                  className="border-white/20 text-white hover:bg-white/10 disabled:opacity-50"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </section>

        <section className="mt-12 sm:mt-16 mb-8 sm:mb-12">
          <Card className="p-6 sm:p-8 bg-white/5 backdrop-blur-sm border-white/10">
            <details>
              <summary className="cursor-pointer text-white font-semibold">About this page</summary>
              <div className="mt-3 text-gray-400 text-sm sm:text-base">
                Browse {categoryName} listings in {cityLabel}. New profiles are added regularly.
              </div>
            </details>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SeoCategoryPage;
